/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FC, useEffect, useMemo, useRef, useState } from "react";
// @ts-ignore: CSS import for DevExtreme theme
import "devextreme/dist/css/dx.light.css";
import { gridStyles } from "./Auditrail.styles";

import DataGrid, {
  Column,
  Paging,
  Pager,
  SearchPanel,
  FilterRow,
  HeaderFilter,
} from "devextreme-react/data-grid";
import { apiService } from "../../services/apiService";
import notify from "devextreme/ui/notify";
import { Button, LoadIndicator } from "devextreme-react";
import { useNavigate } from "react-router-dom";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver";
import { exportDataGrid } from "devextreme/excel_exporter";
// ================= Tipos =================
interface IAuditLog {
  id: string;
  action: string;
  entityName: string;
  entityId: string;
  userName: string;
  timestamp: string; // ISO
  details: string;
  nomina: string;
  sucursalNum: string | null;
  sucursalName: string | null;
}

interface IPagination {
  currentPage: number;
  pageSize: number;
  totalRecords: number;
  totalPages: number;
  totalRecordsFiltered?: number;
}

interface IApiResponse {
  data: IAuditLog[];
  pagination?: IPagination;
  message?: string;
  status?: number;
}

// Endpoint (ajústalo si cambia)
const API_BASE = "/api/Audit/filter";

// ======== Helpers ========
const pad = (n: number) => String(n).padStart(2, "0");
const ymd = (d: Date) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const todayYMD = () => ymd(new Date());

// ======== Componente ========
const BitacoraGrid: FC = () => {
  // Barra superior (búsqueda “externa”, para recargar dataset)
  const [action, setAction] = useState<string>("");
  const [entityName, setEntityName] = useState<string>("");
  const [userName, setUserName] = useState<string>("");

  // por defecto: últimos 7 días
  const defaultEnd = todayYMD();
  const defaultStart = ymd(new Date(Date.now() - 7 * 24 * 3600 * 1000));
  const [startDate, setStartDate] = useState<string>(defaultStart);
  const [endDate, setEndDate] = useState<string>(defaultEnd);

  // Estado de datos locales (lo que verá el grid)
  const [rows, setRows] = useState<IAuditLog[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const dataGridRef = useRef<any>(null);
  const navigate = useNavigate();

  // Filtros aplicados (disparan recarga del dataset desde API)
  const [appliedFilters, setAppliedFilters] = useState<{
    action?: string;
    entityName?: string;
    userName?: string;
    startDate?: string;
    endDate?: string;
  }>({
    action: "",
    entityName: "",
    userName: "",
    startDate: defaultStart,
    endDate: defaultEnd,
  });
  const handleExportExcel = async () => {
    const gridInstance = dataGridRef.current?.instance();

    if (!gridInstance) {
      notify(
        { message: "No se pudo obtener la tabla para exportar." },
        "error",
        3000,
      );
      return;
    }

    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet("Bitacora");

    await exportDataGrid({
      component: gridInstance,
      worksheet,
      autoFilterEnabled: true,

      customizeCell: ({ gridCell, excelCell }) => {
        if (gridCell?.rowType === "header") {
          excelCell.font = { bold: true };
        }
      },
    });

    const buffer = await workbook.xlsx.writeBuffer();

    saveAs(
      new Blob([buffer], { type: "application/octet-stream" }),
      "Bitacora.xlsx",
    );
  };
  const applyFilters = () => {
    if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
      notify(
        { message: "La fecha inicio no puede ser mayor que la fecha fin." },
        "warning",
        3000,
      );
      return;
    }
    setAppliedFilters({
      action: action.trim(),
      entityName: entityName.trim(),
      userName: userName.trim(),
      startDate: startDate || undefined,
      endDate: endDate || undefined,
    });
  };

  // ====== Carga ÚNICA de datos (o cuando cambian filtros externos) ======
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // IMPORTANTE:
        // - Aquí SÍ consultas API, pero solo para traer el "dataset base".
        // - El filtrado/búsqueda del DataGrid (FilterRow, HeaderFilter, SearchPanel)
        //   será 100% local. No hay consultas por cada cambio del grid.
        //
        // Si tu endpoint soporta POST, puedes cambiar a:
        // const resp = await apiService.getRaw<IApiResponse>(API_BASE, { method: "post", data: { ... } });
        const resp = await apiService.getRaw<IApiResponse>(API_BASE, {
          params: {
            // Si tu back permite paginar, puedes pedir un lote grande.
            // Ajusta a tu límite razonable (ej. 5000). Si no hay paginación, omite.
            pageNumber: 1,
            pageSize: 5000,

            // Filtros “externos” (barra superior):
            action: appliedFilters.action || undefined,
            entityName: appliedFilters.entityName || undefined,
            userName: appliedFilters.userName || undefined,
            startDate: appliedFilters.startDate || undefined,
            endDate: appliedFilters.endDate || undefined,
          },
        });

        setRows(resp.data ?? []);
      } catch (err: any) {
        console.error(err);
        notify(
          {
            message: "No fue posible cargar la bitácora.",
            position: "top center",
          },
          "error",
          4000,
        );
        setRows([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [appliedFilters]);

  // Memo opcional si deseas preformatear algo antes de llegar al grid
  const data = useMemo(() => rows, [rows]);

  return (
    <>
      <style>{gridStyles}</style>

      <div className="bitacora-container">
        <h2>Bitácora de Acciones</h2>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            marginBottom: 15,
          }}
        >
          <Button
            icon="chevronleft"
            text="Regresar"
            stylingMode="outlined"
            type="default"
            onClick={() => navigate(-1)}
          />
          {loading && <LoadIndicator visible={true} />}
          <Button
            icon="xlsxfile"
            text="Exportar Excel"
            stylingMode="contained"
            type="default"
            onClick={handleExportExcel}
          />
        </div>

        {/* -------- Barra de filtros “externos” (para recargar dataset) -------- */}
        <div className="filters-bar">
          <div className="filter-field">
            <label>Acción</label>
            <input
              className="filter-input"
              type="text"
              value={action}
              onChange={(e) => setAction(e.target.value)}
              placeholder="e.g. Create, Update…"
            />
          </div>

          {/* Descomenta si quieres filtrar también por entidad en la recarga */}
          {/* <div className="filter-field">
            <label>Entidad</label>
            <input
              className="filter-input"
              type="text"
              value={entityName}
              onChange={(e) => setEntityName(e.target.value)}
              placeholder="e.g. Pedido, Producto…"
            />
          </div> */}

          <div className="filter-field">
            <label>Usuario</label>
            <input
              className="filter-input"
              type="text"
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
              placeholder="e.g. jperez"
            />
          </div>

          <div className="filter-field">
            <label>Fecha inicio</label>
            <input
              className="filter-input"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              max={endDate || undefined}
            />
          </div>

          <div className="filter-field">
            <label>Fecha fin</label>
            <input
              className="filter-input"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              min={startDate || undefined}
            />
          </div>

          <div className="filter-actions">
            <button
              className="btn primary"
              onClick={applyFilters}
              disabled={loading}
            >
              Buscar
            </button>
          </div>
        </div>

        {/* =================== GRID =================== */}
        <DataGrid
          ref={dataGridRef}
          dataSource={data}
          keyExpr="id"
          showBorders
          wordWrapEnabled
          height={600}
          className="custom-grid"
          // Importante: NO usar <RemoteOperations /> aquí
        >
          {/* ✅ Filtros locales por columnas */}
          <HeaderFilter visible={true} allowSearch={true} />
          <FilterRow visible={true} applyFilter="auto" />

          {/* ✅ SearchPanel local */}
          <SearchPanel
            visible={true}
            highlightCaseSensitive={false}
            placeholder="Buscar..."
          />

          {/* Columnas */}
          <Column dataField="userName" caption="Usuario" width={250} />
          <Column dataField="action" caption="Acción" width={160} />
          <Column
            dataField="entityName"
            caption="Entidad"
            width={160}
            visible={false}
          />
          <Column dataField="details" caption="Detalles" minWidth={120} />
          <Column
            dataField="timestamp"
            caption="Fecha y Hora"
            dataType="datetime"
            format="yyyy-MM-dd HH:mm:ss"
            width={280}
          />
          <Column dataField="nomina" caption="Nómina" width={210} />
          <Column dataField="sucursalName" caption="Sucursal" width={170} />

          {/* Paginación (local) */}
          <Paging defaultPageSize={50} />
          <Pager
            visible
            allowedPageSizes={[50, 100, 200]}
            showPageSizeSelector
            showInfo
            showNavigationButtons
          />
        </DataGrid>
      </div>
    </>
  );
};

export default BitacoraGrid;
